import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Check, Clock, AlertTriangle, ListChecks } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Countdown from '@/components/ui/Countdown';
import { fetchTrackCreative, type TrackRow, type TrackStep, type Creative } from '@/lib/queries';
import { cn } from '@/lib/cn';
import { STAGE_LABEL, STAGE_ORDER, STEPS } from '@/lib/steps';

const SHARED_STEP_STAGE: Record<string, string> = Object.fromEntries(STEPS.map((x) => [x.key, x.stage]));

const DOT: Record<TrackStep['state'], string> = {
  done:    'bg-emerald-500 text-white',
  current: 'bg-accent-500 text-white ring-4 ring-accent-500/25',
  blocked: 'bg-red-500 text-white ring-4 ring-red-500/25',
  pending: 'bg-slate-800 text-muted border border-slate-700',
  skipped: 'bg-transparent text-muted/50 border border-dashed border-slate-700',
};

export const STAGES = STAGE_ORDER;
export { STAGE_LABEL };
/** Which stage a step belongs to: clicking the step moves the creative there. The
 *  steps themselves come from lib/steps.ts; these two are extra keys the pipeline
 *  draws but never filters on. */
const STEP_STAGE: Record<string, string> = { ...SHARED_STEP_STAGE, created: 'brief', brief: 'brief' };

function when(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai' }) : '';
}
function short(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', timeZone: 'Asia/Dubai' }) : '';
}
function firstName(by?: string | null): string {
  if (!by) return '';
  // a step not handed out yet names everyone its routing lists ("a, b"): show them all
  const parts = by.split(',').map((x) => x.trim()).filter(Boolean);
  return parts.length > 1 ? parts.join(' / ') : parts[0].split(' ').slice(0, 2).join(' ');
}

/** Steps worth showing: "created" only when it was late. */
function visibleSteps(row: TrackRow): TrackStep[] {
  return row.steps.filter((s) => s.key !== 'created' || s.overdue);
}

/** The step line. Full: one equal column per step, no sideways scroll, a vertical
 *  checklist on phones. Compact: dots only (for lists). The next action sits underneath. */
export default function TrackBar({ row, compact = false, onStep, stage }: { row: TrackRow; compact?: boolean; onStep?: (stage: string) => void; stage?: string }) {
  const steps = visibleSteps(row);
  const n = row.next;
  const at = (s: TrackStep) => STEP_STAGE[s.key] ?? '';
  const clickable = (s: TrackStep) => !!onStep && !!at(s) && at(s) !== stage;
  return (
    <div className="min-w-0">
      {compact ? (
        <div className="flex items-center gap-1">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center" title={`${s.label}${s.by ? ' · ' + s.by : ''}${s.at ? ' · ' + when(s.at) : ''}`}>
              <div className={cn('w-3 h-3 rounded-full shrink-0', DOT[s.state], s.overdue && s.state === 'current' && 'bg-amber-500 ring-amber-500/25')} />
              {i < steps.length - 1 && <div className={cn('h-px w-2', s.state === 'done' ? 'bg-emerald-500/50' : 'bg-slate-800')} />}
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* desktop and tablet: equal columns, nothing scrolls sideways */}
          <div className="hidden sm:grid" style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
            {steps.map((s, i) => (
              <div key={s.key} role={clickable(s) ? 'button' : undefined} tabIndex={clickable(s) ? 0 : undefined}
                onClick={clickable(s) ? () => onStep!(at(s)) : undefined}
                className={cn('relative flex flex-col items-center text-center px-0.5 rounded-lg py-1', clickable(s) && 'cursor-pointer hover:bg-slate-800/40', at(s) === stage && 'bg-accent-600/10')}
                title={`${s.label}${s.at ? ' · ' + when(s.at) : ''}${s.by ? ' · ' + s.by : ''}${s.note ? ' · ' + s.note : ''}${s.due_at ? ' · due ' + when(s.due_at) : ''}${s.overdue ? ' · LATE' : ''}${clickable(s) ? ` · click to move to ${STAGE_LABEL[at(s)]}` : ''}`}>
                {i > 0 && <div className={cn('absolute top-3 right-1/2 w-full h-px', steps[i - 1].state === 'done' ? 'bg-emerald-500/50' : 'bg-slate-800')} />}
                <div className={cn('relative z-10 w-6 h-6 rounded-full flex items-center justify-center shrink-0', DOT[s.state], s.overdue && s.state === 'current' && 'bg-amber-500 ring-amber-500/25')}>
                  {s.state === 'done' && <Check size={12} />}
                  {s.state === 'blocked' && <AlertTriangle size={11} />}
                  {s.state === 'current' && <Clock size={11} />}
                </div>
                <div className={cn('mt-1.5 text-[10px] leading-tight break-words w-full', s.state === 'pending' || s.state === 'skipped' ? 'text-muted/70' : s.state === 'current' ? 'text-accent-300 font-medium' : 'text-muted-strong')}>{s.label}</div>
                {(s.by || s.at) && <div className="text-[9px] text-muted leading-tight w-full truncate">{firstName(s.by)}{s.by && s.at ? ' · ' : ''}{short(s.at)}</div>}
                {/* who the open step waits on ("waiting for Omar"), not only in the tooltip */}
                {(s.state === 'current' || s.state === 'blocked') && s.note && <div className="text-[9px] text-amber-300 leading-tight w-full line-clamp-2" data-testid="step-note">{s.note}</div>}
              </div>
            ))}
          </div>
          {/* phone: a checklist */}
          <div className="sm:hidden space-y-1">
            {steps.map((s) => (
              <div key={s.key} onClick={clickable(s) ? () => onStep!(at(s)) : undefined}
                className={cn('flex items-center gap-2 rounded-lg px-2 py-1.5', s.state === 'current' && 'bg-accent-600/10', s.state === 'blocked' && 'bg-red-500/10', clickable(s) && 'active:bg-slate-800/60', (at(s) === stage || (s.key === 'final' && stage === 'dual_review')) && 'ring-1 ring-accent-500/30')}>
                <div className={cn('w-5 h-5 rounded-full flex items-center justify-center shrink-0', DOT[s.state], s.overdue && s.state === 'current' && 'bg-amber-500 ring-amber-500/25')}>
                  {s.state === 'done' && <Check size={11} />}
                  {s.state === 'blocked' && <AlertTriangle size={10} />}
                  {s.state === 'current' && <Clock size={10} />}
                </div>
                <div className={cn('text-xs flex-1 min-w-0 truncate', s.state === 'pending' || s.state === 'skipped' ? 'text-muted/70' : 'text-muted-strong')}>{s.label}{(s.state === 'current' || s.state === 'blocked') && s.note ? <span className="text-amber-300"> · {s.note}</span> : null}</div>
                <div className="text-[10px] text-muted shrink-0">{firstName(s.by)}{s.by && s.at ? ' · ' : ''}{short(s.at)}</div>
              </div>
            ))}
          </div>
        </>
      )}
      {n && (
        <div className={cn('mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs', !compact && 'rounded-lg border px-3 py-2', !compact && (n.blocked || n.overdue ? 'border-red-500/30 bg-red-500/5' : 'border-slate-800 bg-bg'))}>
          <span className="text-[10px] uppercase tracking-wide text-muted">Next</span>
          <span className={cn('font-medium', n.blocked ? 'text-red-300' : 'text-muted-strong')}>{n.label}</span>
          {n.owner && <span className="text-muted">· {n.owner}</span>}
          {n.key !== 'done' && <span className="text-muted">· {n.days === 0 ? 'today' : `${n.days} day${n.days === 1 ? '' : 's'}`}</span>}
          {n.due_at && n.key !== 'done' && <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-mono', n.overdue ? 'bg-red-500/15 text-red-300' : 'bg-slate-800 text-muted')}>{n.overdue ? 'OVERDUE ' : 'due '}{when(n.due_at)}</span>}
        </div>
      )}
    </div>
  );
}

/** The creative page's one progress card: the step line is the stage control for
 *  planners (click a step to move the creative there), the next action underneath. */
export function TrackCard({ creative, canSteer, pending, onStage }: {
  creative: Creative; canSteer: boolean; pending?: boolean; onStage?: (s: Creative['current_stage']) => void;
}) {
  const q = useQuery({ queryKey: ['track', creative.id], queryFn: () => fetchTrackCreative(creative.id), refetchInterval: 30000 });
  const steer = canSteer && !!onStage && !pending;
  return (
    <Card className="mb-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-medium text-muted-strong text-sm inline-flex items-center gap-2">
          <ListChecks size={14} /> Progress
          <span className="text-[11px] font-normal text-muted">{STAGE_LABEL[creative.current_stage] ?? creative.current_stage}</span>
          {creative.needs_shoot === false && ['video', 'reel', 'photo'].includes(creative.creative_type) && (
            <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-slate-800 text-muted" data-testid="no-shoot-chip">no shoot, existing material</span>
          )}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {creative.stage_due_at && creative.current_stage !== 'posted' && <Countdown due={creative.stage_due_at} />}
          <Link to="/tracker" className="text-[11px] text-accent-400 hover:underline">All creatives</Link>
        </div>
      </div>
      {q.data ? <TrackBar row={q.data} stage={creative.current_stage} onStep={steer ? (st) => onStage!(st as Creative['current_stage']) : undefined} /> : <div className="text-xs text-muted">Loading...</div>}
      {steer && <div className="text-[10px] text-muted mt-2">Click a step to move the creative there.{creative.needs_shoot === false ? '' : ' Video and reels need the raws approved before the edit.'}</div>}
    </Card>
  );
}
